import { Reference, useApolloClient, useMutation } from '@apollo/client'

import i18n from '@app/i18n'

import { useModalStore } from '@features/modal'
import InfoModal from '@features/modal/ui/error'
import styles from '@features/modal/ui/styles.module.scss'

import {
  AddFavoriteProductDocument,
  type AddFavoriteProductMutation,
  RemoveFavoriteProductDocument,
  type RemoveFavoriteProductMutation,
} from '@shared/generated'
import { analytics } from '@shared/lib/analytics'

const modalStore = useModalStore.getState()

const t = i18n.t

const openErrorInfoModal = (errorTransKey?: string) => {
  modalStore.openModal('info', <InfoModal />, {
    title: t('common:modal.error.title'),
    description:
      t(`errors:${errorTransKey}`) ?? t('common:modal.error.description'),
    classes: {
      content: styles['error-info-modal-content'],
    },
  })
}

export const useToggleFavorite = () => {
  const client = useApolloClient()
  const { cache } = client

  const [ addToFavorites ] = useMutation<AddFavoriteProductMutation>(
    AddFavoriteProductDocument,
    {
      update: (cache, _result, options) => {
        const productId = options.context?.productId
        if (!productId) return

        cache.modify({
          id: `Product:${productId}`,
          fields: {
            isFavorite: () => true,
          },
        })

        cache.modify({
          fields: {
            favoriteProducts (existing, { readField, toReference }) {
              const newRef = toReference({
                __typename: 'Product',
                id: productId,
              })
              const exists = existing.edges.some(
                (ref: Reference) => readField('id', ref) === productId,
              )

              if (exists) return existing

              return {
                ...existing,
                edges: [ newRef, ...existing.edges ],
              }
            },
          },
        })

        cache.modify({
          fields: {
            favoriteProducts (existing = {}) {
              return {
                ...existing,
                pageInfo: {
                  ...existing.pageInfo,
                  total: (existing.pageInfo?.total ?? 0) + 1, // или -1 при удалении
                },
              }
            },
          },
        })
      },
    },
  )

  const [ removeFromFavorites ] = useMutation<RemoveFavoriteProductMutation>(
    RemoveFavoriteProductDocument,
    {
      update: (cache, _result, options) => {
        const productId = options.context?.productId
        if (!productId) return

        cache.modify({
          id: `Product:${productId}`,
          fields: {
            isFavorite: () => false,
          },
        })

        cache.modify({
          fields: {
            favoriteProducts (existing, { readField }) {
              if (!existing?.edges) return existing

              return {
                ...existing,
                edges: existing.edges.filter(
                  (edge: Reference) => readField('id', edge) !== productId,
                ),
              }
            },
          },
        })

        cache.modify({
          fields: {
            favoriteProducts (existing = {}) {
              return {
                ...existing,
                pageInfo: {
                  ...existing.pageInfo,
                  total: (existing.pageInfo?.total ?? 0) - 1,
                },
              }
            },
          },
        })
      },
    },
  )

  const toggleFavorite = async (productId: string, isFavorite: boolean) => {
    try {
      if (isFavorite) {
        await removeFromFavorites({
          variables: { productId },
          context: { productId },
        })
      }
      else {
        await addToFavorites({
          variables: { productId },
          context: { productId },
        })
        
        // Track add_to_wishlist event
        analytics.addToWishlist({
          item: {
            id: productId,
            name: '', // Product name not available here
            price: 0, // Price not available here
          },
        })
      }
    }
    catch (error) {
      console.error('Favorite error:', error)
      cache.modify({
        id: `Product:${productId}`,
        fields: {
          isFavorite: () => !isFavorite,
        },
      })
      openErrorInfoModal('favorite_toggle_error')
    }
  }

  return { toggleFavorite }
}
